import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BulkImportPanel } from "../components/BulkImportPanel";
import { CreateEmailZone } from "../components/CreateEmailZone";
import { ImportHtmlPanel } from "../components/ImportHtmlPanel";
import { NewTemplatePanel } from "../components/NewTemplatePanel";
import { PortalShell } from "../components/PortalShell";
import { TemplatePreviewCard } from "../components/TemplatePreviewCard";
import { TemplatePreviewModal } from "../components/TemplatePreviewModal";
import { Button } from "../components/ui/button";
import { ApiError } from "../lib/api";
import { deleteTemplate, duplicateTemplate, listTemplates } from "../lib/templates";

const PAGE_LIMIT = 25;
const SORT_KEY = "portal.templates.sort";
type SortKey = "updated" | "name";

function loadSort(): SortKey {
  const v = typeof localStorage !== "undefined" ? localStorage.getItem(SORT_KEY) : null;
  return v === "name" ? "name" : "updated";
}

/**
 * Templates index — a visual, conversion-oriented screen: an AI-first "Create
 * a new email" zone (3 paths), then your templates as preview cards with
 * search + sort. Replaces the old row list (no thumbnails) per the redesign.
 */
export function TemplatesPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showImportForm, setShowImportForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>(loadSort);
  const [preview, setPreview] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["templates", "list", PAGE_LIMIT],
    queryFn: () => listTemplates({ limit: PAGE_LIMIT, sort: "-updated_at" }),
  });

  const duplicateMutation = useMutation({
    mutationFn: duplicateTemplate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["templates"] }),
  });
  const deleteMutation = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["templates"] }),
  });

  function changeSort(next: SortKey): void {
    setSort(next);
    if (typeof localStorage !== "undefined") localStorage.setItem(SORT_KEY, next);
  }

  const rows = data?.data;
  const templates = rows ?? [];
  const visible = useMemo(() => {
    const list = rows ?? [];
    const q = search.trim().toLowerCase();
    const filtered = q === "" ? list : list.filter((t) => t.name.toLowerCase().includes(q));
    return [...filtered].sort((a, b) =>
      sort === "name"
        ? a.name.localeCompare(b.name)
        : (b.updated_at ?? "").localeCompare(a.updated_at ?? ""),
    );
  }, [rows, search, sort]);

  // A takeover panel (import / bulk / gallery) is the only thing on screen.
  const anyFormOpen = showImportForm || showBulkForm || showGallery;

  return (
    <PortalShell>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-neutral-900">Templates</h1>
          <p className="text-sm text-neutral-600">All email templates in your workspace.</p>
        </div>
        {!anyFormOpen && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowBulkForm(true)}>
              Import zip
            </Button>
            <Button variant="outline" onClick={() => setShowImportForm(true)}>
              Import HTML
            </Button>
          </div>
        )}
      </header>

      {showGallery && (
        <NewTemplatePanel
          onCreated={(id) => navigate(`/templates/${id}/edit`)}
          onCancel={() => setShowGallery(false)}
        />
      )}

      {showImportForm && (
        <ImportHtmlPanel
          onImported={(id, job) =>
            navigate(`/templates/${id}/edit`, { state: { importWarnings: job.warnings ?? [] } })
          }
          onCancel={() => setShowImportForm(false)}
        />
      )}

      {showBulkForm && (
        <BulkImportPanel
          onViewTemplates={() => {
            setShowBulkForm(false);
            void queryClient.invalidateQueries({ queryKey: ["templates"] });
          }}
          onCancel={() => setShowBulkForm(false)}
        />
      )}

      {!anyFormOpen && (
        <div className="space-y-8">
          <CreateEmailZone onBrowse={() => setShowGallery(true)} />

          <section aria-labelledby="your-templates-heading" className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 id="your-templates-heading" className="text-sm font-semibold text-neutral-700">
                Your templates
              </h2>
              {templates.length > 0 && (
                <div className="flex items-center gap-2">
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search…"
                    aria-label="Search templates by name"
                    className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600"
                  />
                  <label className="sr-only" htmlFor="templates-sort">
                    Sort templates
                  </label>
                  <select
                    id="templates-sort"
                    value={sort}
                    onChange={(e) => changeSort(e.target.value as SortKey)}
                    className="rounded-md border border-neutral-300 px-2 py-1.5 text-sm text-neutral-900 focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600"
                  >
                    <option value="updated">Updated</option>
                    <option value="name">Name</option>
                  </select>
                </div>
              )}
            </div>

            {isLoading ? (
              <div
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                aria-label="Loading templates"
              >
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="aspect-[3/4] animate-pulse rounded-lg bg-neutral-100" />
                ))}
              </div>
            ) : error instanceof ApiError ? (
              <p
                role="alert"
                className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
              >
                {error.problem.detail || error.problem.title}
              </p>
            ) : templates.length === 0 ? (
              <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-6 py-10 text-center">
                <p className="text-sm font-medium text-neutral-700">No templates yet</p>
                <p className="mt-1 text-sm text-neutral-500">
                  Generate one with AI, browse the gallery, or start from scratch above.
                </p>
              </div>
            ) : visible.length === 0 ? (
              <p className="px-1 py-6 text-sm text-neutral-500">No templates match “{search}”.</p>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {visible.map((tpl) => (
                  <TemplatePreviewCard
                    key={tpl.id}
                    template={tpl}
                    onPreview={() => setPreview({ id: tpl.id, name: tpl.name })}
                    onDuplicate={() => duplicateMutation.mutate(tpl.id)}
                    onDelete={() => deleteMutation.mutate(tpl.id)}
                    duplicating={duplicateMutation.isPending && duplicateMutation.variables === tpl.id}
                    deleting={deleteMutation.isPending && deleteMutation.variables === tpl.id}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {preview !== null && (
        <TemplatePreviewModal
          templateId={preview.id}
          name={preview.name}
          onClose={() => setPreview(null)}
        />
      )}
    </PortalShell>
  );
}
